import React, { useState } from 'react';
import useGameStore from '../../store/gameStore';
import { toast } from '../ui/sonner';
import { Swords, Handshake } from 'lucide-react';
import { getEnseigne, logoEnseigne } from '../../utils/parc';
import { getVille } from '../../utils/villes';
import { formatMoney } from '../../utils/format';

/* Popup d'interaction concurrente : guerre des prix ou rachat opportuniste. */
export const ConcurrentPopup = () => {
  const ev = useGameStore(s => s.retail?.concurrentEvent);
  const resoudre = useGameStore(s => s.resoudreConcurrent);
  const company = useGameStore(s => s.company);
  const [info, setInfo] = useState(null);
  if (!ev) return null;
  const ens = getEnseigne(ev.enseigneId);
  const ensLogo = logoEnseigne(ev.enseigneId, company?.customLogo ? null : company?.logo);
  const ville = getVille(ev.villeId);
  const agir = (choix) => {
    const res = resoudre(choix);
    if (res?.reason === 'fonds') { toast.error('Trésorerie insuffisante.'); return; }
    if (res?.statut === 'negociation_echouee') { setInfo(`${ens.nom} refuse votre offre à 70 % — le magasin reste à ${formatMoney(ev.prix)}.`); return; }
    if (res?.statut === 'rachete') toast('Magasin racheté !', { description: `L'ancien ${ens.nom} de ${ville?.nom} rejoint votre réseau pour ${formatMoney(res.prix)}.` });
    setInfo(null);
  };
  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="concurrent-popup">
      <div className="w-full max-w-lg rounded-2xl border border-red-500/40 bg-card shadow-2xl overflow-hidden">
        <div className="px-5 py-3 flex items-center gap-2 border-b border-ink-600" style={{ background: `${ens.couleur}22` }}>
          {ev.type === 'guerre' ? <Swords className="w-4 h-4" style={{ color: ens.couleur }} /> : <Handshake className="w-4 h-4" style={{ color: ens.couleur }} />}
          <p className="text-sm font-black text-foreground">{ev.type === 'guerre' ? 'Guerre des prix' : 'Opportunité de rachat'} — {ville?.drapeau} {ville?.nom}</p>
          <span className="ml-auto flex items-center gap-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ background: ens.couleur }} data-testid="concurrent-enseigne-badge">
            {ensLogo && <img src={ensLogo.src} alt={ens.nom} className="w-4 h-4 rounded-full bg-white object-contain" />}
            {ens.nom}
          </span>
        </div>
        <div className="p-5 space-y-3">
          {ev.type === 'guerre' ? (
            <>
              <p className="text-xs text-slate-300">{ens.nom} casse les prix à {ville?.nom} pour asphyxier votre magasin. Comment réagissez-vous ?</p>
              <div className="space-y-2">
                <button onClick={() => agir('suivre')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-suivre">
                  <span className="font-bold text-foreground">S'aligner sur ses prix</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Vous gardez le trafic mais rognez la marge (−1,5 % du CA pendant 10 j).</span>
                </button>
                <button onClick={() => agir('campagne')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-campagne">
                  <span className="font-bold text-foreground">Campagne locale « qualité & conseil » — 600 €</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Vous défendez votre valeur sans toucher aux prix (+4 % trafic 10 j).</span>
                </button>
                <button onClick={() => agir('rien')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-rien">
                  <span className="font-bold text-foreground">Ne rien faire</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">La guerre des prix vous coûtera du trafic sur place (−7 % pendant 10 j).</span>
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-slate-300">Le magasin {ens.nom} de {ville?.nom} est en difficulté : son fonds est à reprendre pour <span className="font-bold text-foreground">{formatMoney(ev.prix)}</span>. Une occasion d'agrandir le réseau à bon prix.</p>
              {info && <p className="text-[11px] text-amber-300 font-bold" data-testid="concurrent-nego-echec">{info}</p>}
              <div className="space-y-2">
                <button onClick={() => agir('racheter')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-racheter">
                  <span className="font-bold text-foreground">Racheter au prix demandé — {formatMoney(ev.prix)}</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Le magasin devient le vôtre immédiatement.</span>
                </button>
                {!info && (
                  <button onClick={() => agir('negocier')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-negocier">
                    <span className="font-bold text-foreground">Négocier à 70 % — {formatMoney(Math.round(ev.prix * 0.7))}</span>
                    <span className="block text-[10px] text-slate-400 mt-0.5">Une chance sur deux qu'il accepte… sinon il campe sur son prix.</span>
                  </button>
                )}
                <button onClick={() => agir('refuser')} className="w-full text-left rounded-lg border border-ink-600 bg-ink-800/60 hover:border-accent p-3 text-xs transition-colors" data-testid="concurrent-choix-refuser">
                  <span className="font-bold text-foreground">Décliner</span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">{ens.nom} restera implanté à {ville?.nom}.</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConcurrentPopup;
